import { useEffect, useRef } from 'react'

const KONAMI_SEQUENCE = [
  'ArrowUp',
  'ArrowUp',
  'ArrowDown',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'ArrowLeft',
  'ArrowRight',
  'b',
  'a',
]

/**
 * Normalises a `KeyboardEvent.key` string by lowercasing the B/A keys
 * the cheat code requires, leaving every other key untouched. Lets the
 * sequence match whether shift is held or not.
 *
 * @param {string} key - the raw key from the keyboard event
 * @returns {string} the normalised key for sequence comparison
 */
const normaliseKonamiKey = (key: string): string => {
  if (key === 'B') {
    return 'b'
  }

  if (key === 'A') {
    return 'a'
  }

  return key
}

/**
 * Listens for the Konami code (↑↑↓↓←→←→ B A) anywhere in the app and
 * fires the callback the first time the full sequence is matched. The
 * sequence resets on a wrong key, so a near-miss doesn't half-trigger.
 *
 * @param {() => void} onUnlock - the callback to fire on full match
 * @returns {void}
 */
const useKonamiCode = (onUnlock: () => void): void => {
  const progressReference = useRef<number>(0)
  const callbackReference = useRef<() => void>(onUnlock)

  useEffect(() => {
    callbackReference.current = onUnlock
  }, [onUnlock])

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      const expected = KONAMI_SEQUENCE[progressReference.current]
      const observedKey = normaliseKonamiKey(event.key)

      if (observedKey === expected) {
        progressReference.current += 1

        if (progressReference.current === KONAMI_SEQUENCE.length) {
          progressReference.current = 0
          callbackReference.current()
        }

        return
      }

      progressReference.current = observedKey === KONAMI_SEQUENCE[0] ? 1 : 0
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [])
}

export { useKonamiCode }
